import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tradingAPI } from '../../services/api';
import { MOBILE_COLORS, MOBILE_SPACING, MOBILE_BORDERS, MOBILE_TYPOGRAPHY, MOBILE_ANIMATIONS } from '../../styles/mobileTheme';
import MobileCard from './MobileCard';
import MobileButton from './MobileButton';
import MobilePercentBadge from './MobilePercentBadge';
import {
  ArrowLeft, TrendingUp, TrendingDown, Globe, Twitter, Send,
  DollarSign, BarChart3, Activity, Target, Award, Clock,
  ChevronUp, ChevronDown, ExternalLink, Star, Share2, Zap,
  ChevronRight, Info, PieChart
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const MobileCoinDetailPage = () => {
  const { coinId } = useParams();
  const navigate = useNavigate();
  
  const [coin, setCoin] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadCoinData();
  }, [coinId, selectedPeriod]);

  const loadCoinData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailsRes, historyRes] = await Promise.all([
        tradingAPI.getCoinDetails(coinId),
        tradingAPI.getCoinHistory(coinId, selectedPeriod),
      ]);
      
      setCoin(detailsRes.data);
      setHistory(historyRes.data?.history || []);
    } catch (err) {
      console.error('Failed to load coin data:', err);
      setError('Не удалось загрузить данные о монете');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, decimals = 2) => {
    if (!value) return '$0.00';
    if (value >= 1) {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
    }
    return `$${value.toPrecision(4)}`;
  };

  const formatCompact = (value) => {
    if (!value) return '$0';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: MOBILE_COLORS.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: MOBILE_SPACING.md,
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${MOBILE_COLORS.border}`,
          borderTopColor: MOBILE_COLORS.primary,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <span style={{ color: MOBILE_COLORS.textSecondary, fontSize: '14px' }}>
          Загрузка {coinId?.toUpperCase()}...
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error || !coin) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: MOBILE_COLORS.background,
        padding: MOBILE_SPACING.xl,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: MOBILE_SPACING.md }}>😕</div>
        <h2 style={{ color: MOBILE_COLORS.textPrimary, marginBottom: MOBILE_SPACING.sm, fontSize: '18px' }}>
          {error || 'Монета не найдена'}
        </h2>
        <p style={{ color: MOBILE_COLORS.textSecondary, marginBottom: MOBILE_SPACING.xl, fontSize: '14px' }}>
          Попробуйте другую монету
        </p>
        <MobileButton onClick={() => navigate(-1)}>
          ← Назад
        </MobileButton>
      </div>
    );
  }

  const priceChange24h = coin.price_change_percentage_24h || 0;
  const isPositive = priceChange24h >= 0;

  // Chart data
  const chartData = history.map(item => ({
    date: new Date(item.timestamp).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    price: item.price,
  }));

  const periods = [
    { value: 1, label: '24ч' },
    { value: 7, label: '7д' },
    { value: 30, label: '30д' },
    { value: 90, label: '90д' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: MOBILE_COLORS.background,
      paddingBottom: '160px', // Space for fixed button + bottom nav
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: MOBILE_COLORS.background,
        borderBottom: `1px solid ${MOBILE_COLORS.border}`,
        padding: MOBILE_SPACING.md,
        paddingTop: 'max(12px, env(safe-area-inset-top))',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: MOBILE_COLORS.backgroundSecondary,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={20} color={MOBILE_COLORS.textPrimary} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.sm }}>
            {coin.image && (
              <img src={coin.image} alt={coin.symbol} style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            )}
            <span style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '700', fontSize: '16px' }}>
              {coin.symbol?.toUpperCase()}
            </span>
          </div>

          <button
            onClick={() => setIsFavorite(!isFavorite)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: MOBILE_BORDERS.radius.full,
              backgroundColor: isFavorite ? MOBILE_COLORS.primaryMuted : MOBILE_COLORS.backgroundSecondary,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Star size={20} color={isFavorite ? MOBILE_COLORS.primary : MOBILE_COLORS.textSecondary} fill={isFavorite ? MOBILE_COLORS.primary : 'none'} />
          </button>
        </div>
      </div>

      {/* Price Section */}
      <div style={{ padding: MOBILE_SPACING.lg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.md, marginBottom: MOBILE_SPACING.sm }}>
          {coin.image && (
            <img src={coin.image} alt={coin.name} style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
          )}
          <div>
            <h1 style={{
              color: MOBILE_COLORS.textPrimary,
              fontSize: '20px',
              fontWeight: '700',
              margin: 0,
            }}>
              {coin.name}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: MOBILE_SPACING.sm }}>
              <span style={{
                backgroundColor: MOBILE_COLORS.backgroundTertiary,
                color: MOBILE_COLORS.textSecondary,
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
              }}>
                {coin.symbol?.toUpperCase()}
              </span>
              {coin.market_cap_rank && (
                <span style={{
                  backgroundColor: MOBILE_COLORS.primaryMuted,
                  color: MOBILE_COLORS.primary,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  Ранг #{coin.market_cap_rank}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Price Display */}
        <div style={{ marginBottom: MOBILE_SPACING.lg }}>
          <div style={{
            fontSize: '36px',
            fontWeight: '700',
            color: MOBILE_COLORS.textPrimary,
            fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono,
            letterSpacing: '-0.02em',
          }}>
            {formatCurrency(coin.current_price)}
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: MOBILE_BORDERS.radius.full,
            backgroundColor: isPositive ? MOBILE_COLORS.successMuted : MOBILE_COLORS.dangerMuted,
            marginTop: MOBILE_SPACING.sm,
          }}>
            {isPositive ? <TrendingUp size={16} color={MOBILE_COLORS.success} /> : <TrendingDown size={16} color={MOBILE_COLORS.danger} />}
            <span style={{
              color: isPositive ? MOBILE_COLORS.success : MOBILE_COLORS.danger,
              fontWeight: '600',
              fontSize: '14px',
            }}>
              {isPositive ? '+' : ''}{priceChange24h.toFixed(2)}%
            </span>
            <span style={{ color: MOBILE_COLORS.textMuted, fontSize: '12px' }}>24ч</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: MOBILE_SPACING.sm,
          marginBottom: MOBILE_SPACING.xl,
        }}>
          <MobileCard padding="md">
            <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
              24ч Макс
            </div>
            <div style={{ color: MOBILE_COLORS.success, fontSize: '14px', fontWeight: '600', fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono }}>
              {formatCurrency(coin.high_24h)}
            </div>
          </MobileCard>
          <MobileCard padding="md">
            <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
              24ч Мин
            </div>
            <div style={{ color: MOBILE_COLORS.danger, fontSize: '14px', fontWeight: '600', fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono }}>
              {formatCurrency(coin.low_24h)}
            </div>
          </MobileCard>
          <MobileCard padding="md">
            <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
              Капитализация
            </div>
            <div style={{ color: MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
              {formatCompact(coin.market_cap)}
            </div>
          </MobileCard>
          <MobileCard padding="md">
            <div style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
              Объём 24ч
            </div>
            <div style={{ color: MOBILE_COLORS.textPrimary, fontSize: '14px', fontWeight: '600' }}>
              {formatCompact(coin.total_volume)}
            </div>
          </MobileCard>
        </div>

        {/* Chart Section */}
        <MobileCard style={{ marginBottom: MOBILE_SPACING.xl }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: MOBILE_SPACING.md,
          }}>
            <span style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '15px' }}>
              График цены
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {periods.map(p => (
                <button
                  key={p.value}
                  onClick={() => setSelectedPeriod(p.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: MOBILE_BORDERS.radius.sm,
                    border: 'none',
                    backgroundColor: selectedPeriod === p.value ? MOBILE_COLORS.primary : MOBILE_COLORS.backgroundTertiary,
                    color: selectedPeriod === p.value ? '#0b0e11' : MOBILE_COLORS.textSecondary,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {chartData.length > 0 ? (
            <div style={{ height: '200px', marginLeft: '-16px', marginRight: '-16px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={isPositive ? MOBILE_COLORS.success : MOBILE_COLORS.danger} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={isPositive ? MOBILE_COLORS.success : MOBILE_COLORS.danger} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: MOBILE_COLORS.textMuted }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={['dataMin', 'dataMax']}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: MOBILE_COLORS.textMuted }}
                    width={60}
                    tickFormatter={(v) => formatCurrency(v, 0)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: MOBILE_COLORS.card,
                      border: `1px solid ${MOBILE_COLORS.border}`,
                      borderRadius: '8px',
                      padding: '8px 12px',
                    }}
                    labelStyle={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px' }}
                    formatter={(value) => [formatCurrency(value), 'Цена']}
                  />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke={isPositive ? MOBILE_COLORS.success : MOBILE_COLORS.danger}
                    strokeWidth={2}
                    fill="url(#priceGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MOBILE_COLORS.textSecondary }}>
              Нет данных для графика
            </div>
          )}
        </MobileCard>

        {/* ATH / ATL Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: MOBILE_SPACING.sm,
          marginBottom: MOBILE_SPACING.xl,
        }}>
          <MobileCard padding="md" style={{ borderColor: MOBILE_COLORS.success + '30' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Award size={14} color={MOBILE_COLORS.success} />
              <span style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px' }}>ATH</span>
            </div>
            <div style={{ color: MOBILE_COLORS.success, fontSize: '15px', fontWeight: '700', fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono }}>
              {formatCurrency(coin.ath)}
            </div>
            {coin.ath_date && (
              <div style={{ color: MOBILE_COLORS.textMuted, fontSize: '10px', marginTop: '4px' }}>
                {formatDate(coin.ath_date)}
              </div>
            )}
            {coin.ath_change_percentage && (
              <div style={{ color: MOBILE_COLORS.danger, fontSize: '11px', marginTop: '2px' }}>
                {coin.ath_change_percentage.toFixed(1)}% от ATH
              </div>
            )}
          </MobileCard>

          <MobileCard padding="md" style={{ borderColor: MOBILE_COLORS.danger + '30' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Target size={14} color={MOBILE_COLORS.danger} />
              <span style={{ color: MOBILE_COLORS.textSecondary, fontSize: '11px' }}>ATL</span>
            </div>
            <div style={{ color: MOBILE_COLORS.danger, fontSize: '15px', fontWeight: '700', fontFamily: MOBILE_TYPOGRAPHY.fontFamilyMono }}>
              {formatCurrency(coin.atl)}
            </div>
            {coin.atl_date && (
              <div style={{ color: MOBILE_COLORS.textMuted, fontSize: '10px', marginTop: '4px' }}>
                {formatDate(coin.atl_date)}
              </div>
            )}
            {coin.atl_change_percentage && (
              <div style={{ color: MOBILE_COLORS.success, fontSize: '11px', marginTop: '2px' }}>
                +{coin.atl_change_percentage.toFixed(0)}% от ATL
              </div>
            )}
          </MobileCard>
        </div>

        {/* Additional Stats */}
        <MobileCard style={{ marginBottom: MOBILE_SPACING.xl }}>
          <div style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '15px', marginBottom: MOBILE_SPACING.md }}>
            Статистика
          </div>
          
          {[
            { label: 'Циркулирующее предложение', value: coin.circulating_supply ? `${(coin.circulating_supply / 1e6).toFixed(2)}M ${coin.symbol?.toUpperCase()}` : '—' },
            { label: 'Общее предложение', value: coin.total_supply ? `${(coin.total_supply / 1e6).toFixed(2)}M` : '∞' },
            { label: 'Макс. предложение', value: coin.max_supply ? `${(coin.max_supply / 1e6).toFixed(2)}M` : '∞' },
            { label: 'FDV', value: formatCompact(coin.fully_diluted_valuation) },
            { label: 'Изменение 7д', value: coin.price_change_percentage_7d ? `${coin.price_change_percentage_7d.toFixed(2)}%` : '—', isChange: true, positive: coin.price_change_percentage_7d >= 0 },
            { label: 'Изменение 30д', value: coin.price_change_percentage_30d ? `${coin.price_change_percentage_30d.toFixed(2)}%` : '—', isChange: true, positive: coin.price_change_percentage_30d >= 0 },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: i < 5 ? `1px solid ${MOBILE_COLORS.border}` : 'none',
              }}
            >
              <span style={{ color: MOBILE_COLORS.textSecondary, fontSize: '13px' }}>{stat.label}</span>
              <span style={{
                color: stat.isChange ? (stat.positive ? MOBILE_COLORS.success : MOBILE_COLORS.danger) : MOBILE_COLORS.textPrimary,
                fontSize: '13px',
                fontWeight: '600',
              }}>
                {stat.value}
              </span>
            </div>
          ))}
        </MobileCard>

        {/* Description */}
        {coin.description && (
          <MobileCard style={{ marginBottom: MOBILE_SPACING.xl }}>
            <div style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '15px', marginBottom: MOBILE_SPACING.md }}>
              О {coin.name}
            </div>
            <p style={{
              color: MOBILE_COLORS.textSecondary,
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0,
            }}>
              {coin.description.slice(0, 300)}
              {coin.description.length > 300 && '...'}
            </p>
          </MobileCard>
        )}

        {/* Links */}
        {(coin.homepage || coin.twitter || coin.telegram) && (
          <MobileCard>
            <div style={{ color: MOBILE_COLORS.textPrimary, fontWeight: '600', fontSize: '15px', marginBottom: MOBILE_SPACING.md }}>
              Ссылки
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: MOBILE_SPACING.sm }}>
              {coin.homepage && (
                <a
                  href={coin.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: MOBILE_COLORS.backgroundTertiary,
                    borderRadius: MOBILE_BORDERS.radius.sm,
                    color: MOBILE_COLORS.textPrimary,
                    textDecoration: 'none',
                    fontSize: '13px',
                  }}
                >
                  <Globe size={14} /> Сайт
                </a>
              )}
              {coin.twitter && (
                <a
                  href={`https://twitter.com/${coin.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: MOBILE_COLORS.backgroundTertiary,
                    borderRadius: MOBILE_BORDERS.radius.sm,
                    color: MOBILE_COLORS.textPrimary,
                    textDecoration: 'none',
                    fontSize: '13px',
                  }}
                >
                  <Twitter size={14} /> Twitter
                </a>
              )}
              {coin.telegram && (
                <a
                  href={coin.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: MOBILE_COLORS.backgroundTertiary,
                    borderRadius: MOBILE_BORDERS.radius.sm,
                    color: MOBILE_COLORS.textPrimary,
                    textDecoration: 'none',
                    fontSize: '13px',
                  }}
                >
                  <Send size={14} /> Telegram
                </a>
              )}
            </div>
          </MobileCard>
        )}
      </div>

      {/* Fixed Bottom Trade Button */}
      <div style={{
        position: 'fixed',
        bottom: '72px', // Above bottom navigation
        left: 0,
        right: 0,
        padding: MOBILE_SPACING.lg,
        paddingBottom: MOBILE_SPACING.lg,
        backgroundColor: MOBILE_COLORS.background,
        borderTop: `1px solid ${MOBILE_COLORS.border}`,
        zIndex: 50,
      }}>
        <Link to={`/trading?symbol=${coin.symbol?.toUpperCase()}USDT`} style={{ textDecoration: 'none' }}>
          <MobileButton fullWidth size="lg">
            <TrendingUp size={18} />
            Торговать {coin.symbol?.toUpperCase()}
          </MobileButton>
        </Link>
      </div>
    </div>
  );
};

export default MobileCoinDetailPage;
